import { openExternal } from './openExternal';

// Global native interceptor for external links (the App-Link loop fix, app-wide).
//
// On native, ANY navigation that lands the in-app WebView on an off-origin http(s) URL can trap
// the user (see openExternal.ts). Patching every call site is whack-a-mole — the research hub has
// dozens of <a target="_blank"> and window.open() sites. So we install TWO catch-alls, native-only:
//
//   (a) a capture-phase document click listener that re-routes external anchor clicks, and
//   (b) a window.open override that re-routes programmatic external opens.
//
// Both funnel through openExternal() (system Custom Tab) instead of the WebView.
//
// WHAT WE DELIBERATELY DO NOT TOUCH — so in-app routing and the OAuth deep-link flow keep working:
//   * same-origin URLs (origin === location.origin)  -> react-router / SPA navigation. Untouched.
//   * relative hrefs ("/partner/...", "#x", "?q=1")   -> these resolve to the SAME origin, so the
//     origin check below classifies them as internal. Untouched.
//   * non-http(s) schemes (cbam://, mailto:, tel:,    -> the protocol guard skips them, so the OAuth
//     intent://, blob:, javascript:)                     deep-link return (cbam://) and mail/phone
//                                                         links behave natively as before.
// Only a URL that BOTH parses as http(s) AND has a different origin is treated as external.

const HTTP_SCHEMES = new Set(['http:', 'https:']);

/**
 * Decide whether `rawHref` (which may be relative) points at an EXTERNAL http(s) origin.
 * Returns the absolute href to hand to openExternal(), or null if it should be left alone.
 *
 * Resolves `rawHref` against the current document URL so relative links are normalised before the
 * origin comparison — a relative link can never be "external", and an absolute same-origin link is
 * still internal.
 */
function resolveExternal(rawHref: string | null | undefined): string | null {
  if (!rawHref) return null;
  let resolved: URL;
  try {
    resolved = new URL(rawHref, window.location.href);
  } catch {
    // Unparseable href (e.g. malformed): not our concern — let the platform handle it.
    return null;
  }
  // Only http(s) can trap the WebView. cbam://, mailto:, tel:, intent://, blob:, javascript: etc.
  // are intentionally left to native handling (this is what preserves the OAuth cbam:// return).
  if (!HTTP_SCHEMES.has(resolved.protocol)) return null;
  // Same-origin => react-router / in-app navigation. Leave it to the SPA.
  if (resolved.origin === window.location.origin) return null;
  return resolved.href;
}

/**
 * Install the native external-link interceptor. No-op on web (returns a no-op disposer).
 * Mount ONCE near the app root, behind isNative(). The returned function removes the listener and
 * restores the original window.open — call it on unmount.
 */
export function installExternalLinkInterceptor(): () => void {
  // CAPTURE phase so we intercept before react-router / other handlers act on the click. We still
  // honour defaultPrevented (another handler already cancelled it) and modified clicks.
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented) return;
    // Let the OS/browser handle modifier and non-primary clicks (middle-click, ctrl/cmd-click).
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const target = e.target as Element | null;
    const anchor = target?.closest?.('a');
    if (!anchor) return;
    // getAttribute('href') (not anchor.href) so we resolve the AUTHORED href ourselves and apply
    // the exact same-origin / scheme logic as the window.open path.
    const external = resolveExternal(anchor.getAttribute('href'));
    if (!external) return;

    e.preventDefault();
    e.stopPropagation();
    void openExternal(external);
  };

  document.addEventListener('click', onClick, true);

  const originalOpen = window.open.bind(window);
  // Override window.open so programmatic external opens are re-routed too. We keep the native call
  // signature loose (the real DOM type has several overloads) and always delegate non-external opens
  // to the original implementation so same-origin popups / about:blank handoffs keep working.
  const patchedOpen = (
    url?: string | URL,
    target?: string,
    features?: string,
  ): Window | null => {
    const external = url != null ? resolveExternal(String(url)) : null;
    if (external) {
      void openExternal(external);
      return null;
    }
    return originalOpen(url as string, target as string, features as string);
  };
  window.open = patchedOpen as typeof window.open;

  return () => {
    document.removeEventListener('click', onClick, true);
    // Only restore if nothing else re-patched window.open after us (avoid clobbering a later owner).
    if (window.open === (patchedOpen as typeof window.open)) {
      window.open = originalOpen as typeof window.open;
    }
  };
}
